import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import "./CreateNewRole.css";

const API_BASE_URL = "https://server.account.majestyfx.com";

export default function CreateNewRole() {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    access_type: "custom",
    permissions: [],
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch all permissions
  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin_get_all_permissions.php`);
      if (response.data.success) {
        setPermissions(response.data.permissions || []);
      }
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setError("Failed to fetch permissions");
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAccessTypeChange = (value) => {
    setFormData({
      ...formData,
      access_type: value,
      permissions: value === "full" ? [] : formData.permissions,
    });
  };

  // Handle permission checkbox change
  const handlePermissionToggle = (permissionId) => {
    setFormData((prev) => {
      const isSelected = prev.permissions.includes(permissionId);
      return {
        ...prev,
        permissions: isSelected
          ? prev.permissions.filter((id) => id !== permissionId)
          : [...prev.permissions, permissionId],
      };
    });
  };

  // Handle select all permissions for a module
  const handleModuleToggle = (moduleName) => {
    const modulePermissions = permissions
      .filter((p) => p.module === moduleName)
      .map((p) => p.id);

    const allSelected = modulePermissions.every((id) =>
      formData.permissions.includes(id)
    );

    setFormData((prev) => {
      if (allSelected) {
        return {
          ...prev,
          permissions: prev.permissions.filter(
            (id) => !modulePermissions.includes(id)
          ),
        };
      } else {
        const newPermissions = [...new Set([...prev.permissions, ...modulePermissions])];
        return {
          ...prev,
          permissions: newPermissions,
        };
      }
    });
  };

  // Group permissions by module
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.module]) {
      acc[permission.module] = [];
    }
    acc[permission.module].push(permission);
    return acc;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation for custom roles
    if (formData.access_type === "custom" && formData.permissions.length === 0) {
      setError("Please select at least one permission for custom role");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        description: formData.description,
        access_type: formData.access_type,
      };

      // Add permissions only for custom roles
      if (formData.access_type === "custom") {
        payload.permissions = formData.permissions;
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin_add_roles.php`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        setSuccess("Role created successfully!");
        setTimeout(() => {
          navigate("/all-role");
        }, 1500);
      } else {
        setError(response.data.message || "Failed to create role");
      }
    } catch (err) {
      console.error("Error creating role:", err);
      setError(err.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-staff-container">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <NavLink to="/">Home</NavLink> /{" "}
        <NavLink to="/all-role">Manage Roles</NavLink> / Create New
      </div>

      {/* Header */}
      <div className="header-row">
        <h2 className="page-title">Create New Dashboard Role</h2>
        <NavLink to="/all-role" className="btn-view-all">
          👁 View All
        </NavLink>
      </div>

      {/* Alerts */}
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Form Card */}
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">
                Role Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter role name"
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="description">
                Description <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Enter role description"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="access_type">
                Access Type <span className="required">*</span>
              </label>
              <select
                id="access_type"
                name="access_type"
                value={formData.access_type}
                onChange={(e) => handleAccessTypeChange(e.target.value)}
                required
              >
                <option value="custom">Custom</option>
                <option value="full">Full Access (Super Admin)</option>
              </select>
              {formData.access_type === "full" && (
                <small style={{ color: "#6b7280", marginTop: "5px", display: "block" }}>
                  Full access grants all permissions automatically
                </small>
              )}
            </div>
          </div>

          {/* Permissions Section - Only show for custom roles */}
          {formData.access_type === "custom" && (
            <div className="form-group permissions-section">
              <label>
                Permissions <span className="required">*</span>
              </label>
              <div className="permissions-container">
                {Object.keys(groupedPermissions).sort().map((module) => (
                  <div key={module} className="permission-module">
                    <div className="module-header">
                      <label className="checkbox-label module-checkbox">
                        <input
                          type="checkbox"
                          checked={groupedPermissions[module].every((p) =>
                            formData.permissions.includes(p.id)
                          )}
                          onChange={() => handleModuleToggle(module)}
                        />
                        <strong>{module}</strong>
                      </label>
                    </div>
                    <div className="permission-list">
                      {groupedPermissions[module].map((permission) => (
                        <label
                          key={permission.id}
                          className="checkbox-label permission-item"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission.id)}
                            onChange={() => handlePermissionToggle(permission.id)}
                          />
                          <span>
                            {permission.name}
                            <small className="permission-desc">
                              {permission.description}
                            </small>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {formData.permissions.length === 0 && (
                <small style={{ color: "#ef4444", marginTop: "5px", display: "block" }}>
                  Please select at least one permission
                </small>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate("/all-role")}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? "Creating..." : "Create Role"}
            </button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="footer">
        <p>
          Copyright © 2025 <span className="brand">Ample Markets</span>. All
          rights reserved.
        </p>
        <p className="version">Version 2.0</p>
      </div>
    </div>
  );
}

